import { filterClasses } from "@swivl-mono/helpers";
import { DEFAULT_HOLIDAY_APPLIES_TO_SLUGS, HolidayRuleInterface, ScheduleGroup } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Checkbox, Input, Select, SelectSize } from "@swivl-mono/ui";
import { useState } from "react";
import { TbX } from "react-icons/tb";
import { rrulestr } from "rrule";

import { ClosurePeriodFields, ClosureTypeChoice, closureSummary, useClosureDraft } from "./ClosureFields";
import { InheritancePill } from "./InheritancePill";
import { PageScope, isRowOwnedAtScope, rowScopeKind } from "./Scope";
import { validateHolidayRrule } from "./validateHolidayRrule";

interface HolidayRuleListProps {
  /** Rules already persisted and visible from this scope. */
  rules: HolidayRuleInterface[];
  /** Added in this edit session, not yet written. */
  staged: Partial<HolidayRuleInterface>[];
  /** Saved rules removed in this session, not yet written. */
  deletedIds: number[];
  /** The parent card is in edit mode — show add / remove affordances. */
  isEditing: boolean;
  onStage: (rule: Partial<HolidayRuleInterface>) => void;
  onUnstage: (index: number) => void;
  onDelete: (ruleId: number) => void;
  scope: PageScope;
  /** The schedule groups a rule may close: built-ins plus the org's custom groups (decision 50). */
  groups: ScheduleGroup[];
}

const CUSTOM_RRULE = "custom";

const PRESETS: { label: string; rrule: string }[] = [
  { label: "Christmas Day (Dec 25)", rrule: "FREQ=YEARLY;BYMONTH=12;BYMONTHDAY=25" },
  { label: "New Year's Day (Jan 1)", rrule: "FREQ=YEARLY;BYMONTH=1;BYMONTHDAY=1" },
  { label: "Independence Day (Jul 4)", rrule: "FREQ=YEARLY;BYMONTH=7;BYMONTHDAY=4" },
  { label: "Thanksgiving (4th Thu in Nov)", rrule: "FREQ=YEARLY;BYMONTH=11;BYDAY=4TH" },
  { label: "Memorial Day (last Mon in May)", rrule: "FREQ=YEARLY;BYMONTH=5;BYDAY=-1MO" },
  { label: "Labor Day (1st Mon in Sep)", rrule: "FREQ=YEARLY;BYMONTH=9;BYDAY=1MO" }
];

function friendlyRrule(rrule: string): string {
  try {
    return rrulestr(rrule).toText();
  } catch {
    return rrule;
  }
}

function formatSlugs(slugs: string[], groups: ScheduleGroup[]): string {
  if (slugs.length === 0) return "no schedules (rule disabled)";
  return slugs.map((slug) => groups.find((group) => group.slug === slug)?.label ?? slug).join(", ");
}

/**
 * Holiday rules for the current scope, listed read-only with provenance tags
 * and editable while the parent card is in edit mode.
 *
 * `appliesToSlugs` lets a rule close some schedules but not others — the
 * storage-industry norm is for gate access to stay open every day, so it's left
 * unchecked by default. Rules inherited from a broader scope are shown but
 * can't be removed here; they're managed at the scope that owns them.
 *
 * Editing an existing rule in place is a follow-up; today it's delete + re-add.
 *
 * Adds and removals are staged rather than written immediately — the card's Save
 * commits them, and Cancel drops them.
 */
export function HolidayRuleList({
  rules,
  staged,
  deletedIds,
  isEditing,
  onStage,
  onUnstage,
  onDelete,
  scope,
  groups
}: HolidayRuleListProps): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftPreset, setDraftPreset] = useState(PRESETS[0]!.rrule);
  const [draftCustomRrule, setDraftCustomRrule] = useState("");
  const [draftSlugs, setDraftSlugs] = useState<string[]>([...DEFAULT_HOLIDAY_APPLIES_TO_SLUGS]);
  const closure = useClosureDraft();

  const rrule = draftPreset === CUSTOM_RRULE ? draftCustomRrule.trim() : draftPreset;
  const isCustom = draftPreset === CUSTOM_RRULE;
  // Presets are known-good; only a typed rule needs checking. Stay quiet while
  // the field is empty (Add is disabled anyway), matching the URL field.
  const customRruleError = isCustom && rrule ? validateHolidayRrule(rrule) : undefined;
  const canAdd = !!draftLabel.trim() && !!rrule && !customRruleError && closure.isValid;
  const visible = rules.filter((r) => !deletedIds.includes(r.id));
  const isEmpty = visible.length === 0 && staged.length === 0;

  const toggleSlug = (slug: string): void => {
    setDraftSlugs((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const resetDraft = (): void => {
    setDraftLabel("");
    setDraftPreset(PRESETS[0]!.rrule);
    setDraftCustomRrule("");
    setDraftSlugs([...DEFAULT_HOLIDAY_APPLIES_TO_SLUGS]);
    closure.reset();
    setAdding(false);
  };

  const handleAdd = (): void => {
    if (!canAdd) return;
    onStage({
      groupId: scope.kind === "group" ? (scope.groupId ?? null) : null,
      locationId: scope.kind === "location" ? (scope.locationId ?? null) : null,
      label: draftLabel.trim(),
      rrule,
      isClosed: closure.type === "closed",
      periods: closure.toPeriods(),
      appliesToSlugs: draftSlugs
    });
    resetDraft();
  };

  return (
    <div>
      {!isEmpty && (
        <div>
          {visible.map((r) => (
            <HolidayRow
              key={r.id}
              label={r.label}
              rrule={r.rrule}
              summary={closureSummary(r.isClosed, r.periods)}
              slugs={formatSlugs(r.appliesToSlugs ?? [], groups)}
              // Every row carries its provenance, including rows owned here, so
              // the tag column reads consistently with the other Hours cards.
              pill={<InheritancePill source={rowScopeKind(r)} currentScope={scope.kind} />}
              // Inherited rules are shown for context but belong to the scope
              // that owns them — removing one has to happen there.
              onRemove={isEditing && isRowOwnedAtScope(r, scope) ? () => onDelete(r.id) : undefined}
            />
          ))}
          {staged.map((r, idx) => (
            <HolidayRow
              key={`staged-${idx}`}
              label={r.label ?? ""}
              rrule={r.rrule ?? ""}
              summary={closureSummary(r.isClosed === true, r.periods)}
              slugs={formatSlugs(r.appliesToSlugs ?? [], groups)}
              pill={<span className="body-xs rounded-xs bg-grey-100 px-1 py-0.5 text-grey-700">Unsaved</span>}
              onRemove={isEditing ? () => onUnstage(idx) : undefined}
            />
          ))}
        </div>
      )}

      {/* Add sits below the list, so it stays next to where a new rule appears.
          Shares a row with the empty-state message rather than stacking. */}
      {(isEmpty || (isEditing && !adding)) && (
        <div className={filterClasses("mx-4 flex items-center justify-between gap-4 py-3", !isEmpty && "border-t border-grey-200")}>
          <p className="body-sm text-grey-500">{isEmpty ? "No holiday rules configured." : null}</p>
          {isEditing && !adding && (
            <Button variant={ButtonVariant.TEXT} onClick={() => setAdding(true)}>
              Add holiday
            </Button>
          )}
        </div>
      )}

      {adding && (
        <div className="mx-4 my-3 space-y-3 rounded-sm border border-grey-200 p-3">
          <Input label="Label" required value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} placeholder="e.g. Christmas Day" />

          <div>
            <p className="body-sm-alt text-grey-600">Recurrence</p>
            <div className="mt-1">
              <Select
                size={SelectSize.MEDIUM}
                hideClear
                selectedValues={[draftPreset]}
                options={[...PRESETS.map((p) => ({ label: p.label, value: p.rrule })), { label: "Custom recurrence rule…", value: CUSTOM_RRULE }]}
                onChange={(values) => {
                  if (values[0] !== undefined) setDraftPreset(values[0]);
                }}
              />
            </div>
            {isCustom && (
              <Input
                label="Custom recurrence rule"
                required
                containerClassName="mt-2"
                value={draftCustomRrule}
                error={customRruleError}
                onChange={(e) => setDraftCustomRrule(e.target.value)}
                placeholder="e.g. FREQ=YEARLY;BYMONTH=12;BYMONTHDAY=25"
              />
            )}
          </div>

          <div>
            <p className="body-sm-alt text-grey-600">Affects which schedules?</p>
            <div className="mt-1 flex flex-wrap gap-4">
              {groups.map((m) => (
                <Checkbox
                  key={m.slug}
                  name={`holiday-slug-${m.slug}`}
                  classNames={{ label: "body-sm" }}
                  label={m.label}
                  checked={draftSlugs.includes(m.slug)}
                  onCheckedChange={() => toggleSlug(m.slug)}
                />
              ))}
            </div>
            <p className="body-sm mt-1 text-grey-600">Gate access usually stays open on holidays, so it's left unchecked.</p>
          </div>

          <ClosureTypeChoice name="holiday-closure" label="On this day, the affected schedules are" closure={closure} />
          <ClosurePeriodFields closure={closure} />

          <div className="flex items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={resetDraft}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} onClick={handleAdd} disabled={!canAdd}>
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface HolidayRowProps {
  label: string;
  rrule: string;
  summary: string;
  slugs: string;
  /** Omitted for rows owned at the scope being viewed. */
  pill?: React.ReactNode;
  /** Omitted when the row can't be removed from this scope. */
  onRemove?: () => void;
}

function HolidayRow({ label, rrule, summary, slugs, pill, onRemove }: HolidayRowProps): React.ReactElement {
  return (
    <div className="mx-4 grid grid-cols-[200px_1fr_auto] items-start gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
      <span className="body-sm-alt text-grey-900">{label}</span>
      <div className="min-w-0">
        <p className="body-sm text-grey-900">{friendlyRrule(rrule)}</p>
        <p className="body-sm mt-0.5 text-grey-600">{summary}</p>
        <p className="body-sm mt-0.5 text-grey-600">Affects: {slugs}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {pill}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="cursor-pointer text-grey-400 hover:text-red-500"
            title="Remove this holiday"
            aria-label={`Remove ${label}`}
          >
            <TbX className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}
